import { Box, Divider, IconButton, Typography } from '@mui/material';
import DeleteIcon from 'assets/delete-red.png';
import { ReactComponent as EditIcon } from 'assets/edit.svg';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import CustomDrawer from 'components/shared/CustomDrawer/CustomDrawer';
import CustomNotes from 'components/shared/CustomNotes';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import React from 'react';
import { modalReducer } from 'reducers/modalReducer';
import { ContactService } from 'service/contact.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import { useContactNotes } from '../hooks/useContactNotes';
import ContactFields from './ContactFields/ContactFields';
import ContactHeader from './ContactHeader/ContactHeader';
import style from './ContactsView.module.css';

const ContactsView = ({
  setDrawerState,
  drawerState,
  selectedData,
  setAnchorEl,
  setAddContactOpen,
  setSelectedData,
  onDeleteSuccess,
}: {
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
  selectedData: any;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLDivElement | null>>;
  setAddContactOpen: React.Dispatch<React.SetStateAction<boolean>>;
  notesData?: any;
  onDeleteSuccess: () => void;
  setSelectedData: React.Dispatch<any>;
}) => {
  const { alertState, startOperation, completeOperation, getAlertContent, setAlertState } =
    useAlertStatus();
  const [modalState, modalDispatch] = React.useReducer(modalReducer, { delete: false });
  const manager = useContactNotes(
    selectedData?.contactId || selectedData?.id,
    startOperation,
    completeOperation,
  );

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    modalDispatch({ type: 'OPEN', payload: 'delete' });
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await ContactService.deleteContact(selectedData?.contactId || selectedData?.id);

      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      setDrawerState(false);
    } catch (error) {
      completeOperation('delete', 'Failed to delete contact', true);
      return;
    }
    onDeleteSuccess();
  };
  const onClose = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setSelectedData(null);
    setDrawerState(open);
  };
  const handleEdit = () => {
    setAddContactOpen(true);
    setAnchorEl(null);
  };

  return (
    <CustomDrawer state={drawerState} onClose={onClose(false)}>
      <Box sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'scroll' }}>
        <Box
          sx={{ position: 'sticky', top: 0, backgroundColor: zeplinColor.Neutral1, zIndex: '1' }}
        >
          <Box
            sx={{
              display: 'flex',

              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px',
            }}
          >
            <Typography variant='responsiveHeading2'>Contact Details</Typography>
            <Box
              sx={{
                display: 'flex',

                alignItems: 'center',
                gap: '12px',
              }}
            >
              <LightTooltip
                title={
                  <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                    Edit
                  </Typography>
                }
                placement='bottom'
              >
                <IconButton size='small' onClick={handleEdit}>
                  <EditIcon width={20} height={20} />
                </IconButton>
              </LightTooltip>
              <LightTooltip
                title={
                  <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                    Delete
                  </Typography>
                }
                placement='bottom'
              >
                <IconButton size='small' onClick={handleDelete}>
                  <img src={DeleteIcon} width={20} height={20} alt='delete' />
                </IconButton>
              </LightTooltip>
              <Divider
                orientation='vertical'
                flexItem
                sx={{
                  borderColor: zeplinColor.Neutral5,
                  height: '24px',
                  margin: '0 4px',
                }}
              />
              <img
                src={'/assets/close-icon.svg'}
                className={style['icon']}
                alt='close'
                style={{ cursor: 'pointer' }}
                onClick={onClose(false)}
              />
            </Box>
          </Box>
          <Divider
            component='div'
            sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
          />
        </Box>
        <Box sx={{ padding: { xs: '20px', xl: '24px' } }}>
          <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '24px' }, flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '32px' }, flexDirection: 'column' }}>
              <ContactHeader selectedData={selectedData} />

              <Divider
                component='div'
                sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                Personal Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <ContactFields
                  icon={'/assets/role.svg'}
                  fieldName='Client name'
                  fieldValue={selectedData?.client[0]?.Name || 'N/A'}
                  isProfile
                  src={selectedData?.client[0]?.profile_image_url || ''}
                />
                <ContactFields
                  icon={'/assets/company-icon.svg'}
                  fieldName='Company name'
                  fieldValue={selectedData?.company || 'N/A'}
                />
                <ContactFields
                  icon={'/assets/contact-card-icon.svg'}
                  fieldName='Role'
                  fieldValue={selectedData?.roles || 'N/A'}
                />
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <img
                      src={'/assets/call-icon.svg'}
                      alt='Contact'
                      width={'20px'}
                      height={'20px'}
                    />
                    <Typography
                      variant='responsiveBody3'
                      sx={{ color: zeplinColor.Neutral8, minWidth: '10rem' }}
                    >
                      Phone
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Typography variant='responsiveBody2'>
                      {selectedData?.phone || 'N/A'}
                    </Typography>
                    <img src='/assets/copy-view.png' alt='Contact' width={'18px'} height={'18px'} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '8px' }}>
                  <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <img src={'/assets/email.svg'} alt='Contact' width={'20px'} height={'20px'} />
                    <Typography
                      variant='responsiveBody3'
                      sx={{ color: zeplinColor.Neutral8, minWidth: '10rem' }}
                    >
                      Email
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: '8px' }}>
                    <Typography variant='responsiveBody2' color={zeplinColor.Brand1}>
                      {selectedData?.email || 'N/A'}
                    </Typography>
                    <img src='/assets/copy-view.png' alt='Contact' width={'18px'} height={'18px'} />
                  </Box>
                </Box>
                <ContactFields
                  icon={'/assets/user-multiple.svg'}
                  fieldName='Manager'
                  fieldValue={selectedData?.manager[0]?.Name || 'N/A'}
                  isProfile
                  src={selectedData?.manager[0]?.profile_image_url || ''}
                />
              </Box>
            </Box>
            <Divider
              component='div'
              sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                System Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <ContactFields
                  icon={'/assets/created-by-icon.svg'}
                  fieldName='Created by'
                  fieldValue={selectedData?.createdByName || 'N/A'}
                  isProfile
                  src={selectedData?.createdByProfileImageUrl}
                />
                <ContactFields
                  icon={'/assets/calendar-icon.svg'}
                  fieldName='Created date'
                  fieldValue={
                    selectedData?.createdAt ? Utilities.formatDate(selectedData.createdAt) : 'N/A'
                  }
                />
                <ContactFields
                  icon={'/assets/clock-icon.svg'}
                  fieldName='Last contacted'
                  fieldValue={
                    selectedData?.lastContacted
                      ? Utilities.formatDate(selectedData.lastContacted)
                      : 'N/A'
                  }
                />
              </Box>
            </Box>
            <Divider
              component='div'
              sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <CustomNotes manager={manager} />
            </Box>
          </Box>
        </Box>
      </Box>
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={handleConfirmDelete}
        title='Delete Contact'
        description='Are you sure you want to delete this contact? This action cannot be undone.'
      />
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </CustomDrawer>
  );
};

export default ContactsView;
